import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { useToast } from "../../../mantine/notifications/useToast";
import type { MeteringServiceProvider } from "../../../utils/backend-types";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { getCategoryColumn } from "./columns/getCategoryColumn";
import { getHistoryColumn } from "./columns/getHistoryColumn";
import { getImportFrequencyColumn } from "./columns/getImportFrequencyColumn";
import { useMeteringServiceProviders } from "./hooks/useMeteringServiceProvider";
import { useMeteringServiceProviderMutations } from "./hooks/useMeteringServiceProviderMutations";
import "./MeteringServiceProviderTable.scss";

const METERING_SERVICE_PROVIDER_FIELD_LABELS = {
  id: "ID",
  name: "Name",
  marktpartneridentifikationsnummer: "MP-ID",
  category: "Kategorie",
  medium: "Messstellenmedium",
  importFrequency: "Versandfrequenz",
  email: "E-Mail",
  notes: "Notizen",
  customLabel: "Benutzerdefiniertes Label"
} as const satisfies { [key in keyof MeteringServiceProvider]: string };

const cols: Array<MRT_ColumnDef<MeteringServiceProvider>> = [
  {
    accessorKey: "name",
    header: METERING_SERVICE_PROVIDER_FIELD_LABELS.name,
    enableEditing: false
  },
  {
    accessorKey: "marktpartneridentifikationsnummer",
    header:
      METERING_SERVICE_PROVIDER_FIELD_LABELS.marktpartneridentifikationsnummer,
    size: 160,
    enableEditing: false
  },
  getCategoryColumn(),
  {
    accessorKey: "medium",
    header: METERING_SERVICE_PROVIDER_FIELD_LABELS.medium,
    size: 120,
    enableEditing: false,
    Cell: ({ renderedCellValue }) => (
      <span>{renderedCellValue ? renderedCellValue : "Unbekannt"}</span>
    )
  },
  getImportFrequencyColumn(),
  {
    accessorKey: "email",
    header: METERING_SERVICE_PROVIDER_FIELD_LABELS.email
  },
  {
    accessorKey: "notes",
    header: METERING_SERVICE_PROVIDER_FIELD_LABELS.notes
  },
  {
    accessorKey: "customLabel",
    header: METERING_SERVICE_PROVIDER_FIELD_LABELS.customLabel
  },
  getHistoryColumn()
];

function MeteringServiceProviderTable() {
  const {
    data: meteringServiceData,
    isLoading: isMeteringServiceLoading,
    error: meteringServiceError
  } = useMeteringServiceProviders();
  const { updateMutation } = useMeteringServiceProviderMutations();
  const { showErrorToast } = useToast();

  const table = useMantineReactTable<MeteringServiceProvider>({
    ...getDefaultMRTOptions<MeteringServiceProvider>({
      initialState: { sorting: [{ id: "name", desc: false }] },
      hideBorder: true
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: { text: "Bearbeiten" }
      }
    }),
    onEditingRowSave: async ({ values }) => {
      try {
        await updateMutation.mutateAsync({
          id: values.id,
          newData: Object.keys(values).reduce((result, key) => {
            if (values[key] !== undefined && values[key] !== null) {
              result[key] = values[key];
            }
            return result;
          }, {} as MeteringServiceProvider)
        });
        table.setEditingRow(null);
      } catch (error) {
        showErrorToast(error);
      }
    },
    columns: cols,
    data: meteringServiceData || []
  });

  return (
    <LoadOrError
      error={meteringServiceError}
      loading={isMeteringServiceLoading}
    >
      <MantineReactTable table={table} />
    </LoadOrError>
  );
}

export { METERING_SERVICE_PROVIDER_FIELD_LABELS, MeteringServiceProviderTable };
